import styles from './index.module.css'
import { Badge } from '@radix-ui/themes'
import type { BadgeProps } from '@radix-ui/themes'
import React from 'react'
import type { Playlist } from 'types/playlist'
import { PLAYLIST } from '@disco/constants/ItemTypes'
import { formatTimestamp, formatTimestampShort } from '@disco/utils/time-utils'
import ColorIndicators from 'components/common/color-indicators'
import PlaylistShowcaseArtwork from 'components/common/playlist-showcase-artwork'
import WatermarkingStatusFlag from 'components/explorer/watermarking-status-flag'
import Flex from 'components/ui/flex'
import Lozenge from 'components/ui/lozenge'
import MediaItem from 'components/ui/media-item'
import Skeleton from 'components/ui/skeleton'
import TextOverflow from 'components/ui/text-overflow'
import { useSelector } from 'hooks/use-redux'
import { getOwner } from './utils'
type Props = {
  hideWatermarkingFlag?: boolean
  playlist: Playlist | null | undefined
  showcase?: boolean
  libraryGroupType?: string
}

type OwnerBadgeProps = {
  firstName: string | null | undefined
  lastName: string | null | undefined
  email: string | null | undefined
}

// Mirrors the hue families of the old getTextColor palette (teal, coral,
// violet, rose, blue, gold), swapped for real Radix accent names so
// variant="soft" renders its actual tinted-background treatment.
const ownerBadgeColors: BadgeProps['color'][] = [
  'teal',
  'orange',
  'violet',
  'pink',
  'blue',
  'amber',
]

const getOwnerBadgeColor = (name: string): BadgeProps['color'] => {
  let code = 0
  name.split('').forEach(char => (code += char.charCodeAt(0)))
  return ownerBadgeColors[code % ownerBadgeColors.length]
}

/**
 * Owner initials badge for playlist rows. Same initials logic as E2Avatar,
 * rendered as a Radix Badge (soft variant, size 1) instead of the legacy
 * `.e2avatar` div — scoped to playlist rows only. Color is a Radix accent
 * name hashed from the owner's name/email, not the arbitrary hex E2Avatar
 * used, so the soft variant's real tinted-background rendering applies.
 */
const OwnerBadge = ({ firstName, lastName, email }: OwnerBadgeProps) => {
  const isCurrent = useSelector(state => state.user.email === email)
  const resolvedEmail = email || '-' // needs real name
  const initials =
    firstName && lastName
      ? firstName[0] + lastName[0]
      : resolvedEmail.slice(0, 2).toUpperCase()
  const name =
    firstName && lastName ? `${firstName} ${lastName}` : resolvedEmail
  return (
    <Badge
      className={styles.ownerBadge}
      variant="soft"
      size="1"
      color={isCurrent ? 'gray' : getOwnerBadgeColor(initials)}
      title={name}
    >
      {initials}
    </Badge>
  )
}

/**
 * Playlist information display component with owner, timestamp, and status indicators
 * Shows playlist details with color indicators, client version badges, and watermarking status
 * @param hideWatermarkingFlag - Whether to hide watermarking status indicators
 * @param playlist - Playlist data to display, null shows skeleton loading
 * @param showcase - Whether to display showcase artwork
 * @param libraryGroupType - Type of library group for owner display logic
 */
const PlaylistInfo = ({
  hideWatermarkingFlag,
  playlist,
  showcase,
  libraryGroupType,
}: Props) => {
  if (!playlist) {
    return (
      <MediaItem
        media={showcase ? <PlaylistShowcaseArtwork /> : null}
        title={<Skeleton />}
        caption={<Skeleton />}
      />
    )
  }

  const {
    is_client_version,
    name,
    owner,
    playlist_tags,
    timestamp,
    watermarking_status,
  } = playlist

  const renderOwner = () => {
    const libraryOwner = getOwner(playlist, libraryGroupType)
    return libraryOwner ? (
      <div className={styles.owner}>
        <TextOverflow content={libraryOwner} />
      </div>
    ) : owner?.member_of_business ? (
      <OwnerBadge
        firstName={owner.first_name}
        lastName={owner.last_name}
        email={owner.email}
      />
    ) : null
  }

  return (
    <MediaItem
      media={showcase ? <PlaylistShowcaseArtwork /> : null}
      title={name || 'Untitled playlist'}
      caption={
        <Flex spacing="small">
          {owner && renderOwner()}
          <Flex.Content>
            <div className={styles.date} title={formatTimestamp(timestamp)}>
              {formatTimestampShort(timestamp)}
            </div>
          </Flex.Content>
          {is_client_version && <Lozenge text="Client" />}
          {playlist_tags && <ColorIndicators tags={playlist_tags} />}
          {!hideWatermarkingFlag && watermarking_status && (
            <WatermarkingStatusFlag
              short
              type={PLAYLIST}
              watermarking_status={watermarking_status}
            />
          )}
        </Flex>
      }
    />
  )
}

export default React.memo<Props>(PlaylistInfo)
